/**
 * Imports API - For triggering and monitoring import jobs
 */
import { apiClient } from "./client";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

export interface OsmImportRequest {
    boundingBox?: {
        minLat: number;
        minLon: number;
        maxLat: number;
        maxLon: number;
    };
    notes?: string;
}

export interface LantmaterietImportRequest {
    filePath?: string;
    notes?: string;
}

export interface ElectoralDistrictImportRequest {
    filePath?: string;
    notes?: string;
}

export interface PostalCodeImportRequest {
    filePath?: string;
    notes?: string;
}

export interface MunicipalityImportRequest {
    source?: string; // e.g. "kommuner" – must match MunicipalityImport:Sources in appsettings
    notes?: string;
}

export interface SpatialAssignmentRequest {
    notes?: string;
}

export interface MunicipalityFromElectoralDistrictsRequest {
    notes?: string;
}

export interface AddressGridAggregationsRequest {
    campaignId: string; // GUID
    precisions?: number[]; // Optional: defaults to [3, 4, 5]
    notes?: string;
}

export interface ImportJobResult {
    jobId: string;
    context: {
        source: string;
        correlationId: string;
        requestedByUserId: string;
        notes?: string;
    };
}

/** Result when filling electoral metadata for a whole län (county). */
export interface ElectoralMetadataFillForLanResult {
    lanId: string;
    count: number;
    jobIds: string[];
}

/**
 * Check if current user has access to Imports (SuperAdmin only). Returns true if 204, false on 403.
 */
export const checkImportsAccess = async (): Promise<boolean> => {
    try {
        await apiClient.get("/imports/access");
        return true;
    } catch {
        return false;
    }
};

/**
 * React Query hook for checking Imports access (SuperAdmin only).
 */
export const useImportsAccess = () => {
    return useQuery({
        queryKey: ["imports", "access"],
        queryFn: checkImportsAccess,
        retry: false,
        staleTime: 5 * 60 * 1000, // 5 minutes
    });
};

/**
 * Triggers an OSM import job
 */
export const triggerOsmImport = async (request: OsmImportRequest): Promise<ImportJobResult> => {
    const response = await apiClient.post<ImportJobResult>("/imports/osm", request);
    return response.data;
};

/**
 * Triggers a Lantmäteriet import job
 */
export const triggerLantmaterietImport = async (
    request: LantmaterietImportRequest
): Promise<ImportJobResult> => {
    const response = await apiClient.post<ImportJobResult>("/imports/lm", request);
    return response.data;
};

/**
 * Triggers an Electoral District import job
 */
export const triggerElectoralDistrictImport = async (
    request: ElectoralDistrictImportRequest
): Promise<ImportJobResult> => {
    const response = await apiClient.post<ImportJobResult>("/imports/electoral-districts", request);
    return response.data;
};

/**
 * Triggers a Postal Code import job
 */
export const triggerPostalCodeImport = async (
    request: PostalCodeImportRequest
): Promise<ImportJobResult> => {
    const response = await apiClient.post<ImportJobResult>("/imports/postal-codes", request);
    return response.data;
};

/**
 * Triggers a Municipality (kommun) import job
 */
export const triggerMunicipalityImport = async (
    request: MunicipalityImportRequest
): Promise<ImportJobResult> => {
    const response = await apiClient.post<ImportJobResult>("/imports/municipalities", request);
    return response.data;
};

/**
 * Triggers a job that rebuilds municipality polygons by dissolving electoral district polygons by municipality code.
 * Run after electoral district import to get high-quality municipality boundaries.
 */
export const triggerMunicipalityFromElectoralDistricts = async (
    request: MunicipalityFromElectoralDistrictsRequest = {}
): Promise<ImportJobResult> => {
    const response = await apiClient.post<ImportJobResult>(
        "/imports/municipality-from-electoral-districts",
        request
    );
    return response.data;
};

/**
 * Triggers a Spatial Assignment job (rebuilds road segment assignments, electoral districts, postal codes)
 */
export const triggerSpatialAssignment = async (
    request: SpatialAssignmentRequest = {}
): Promise<ImportJobResult> => {
    const response = await apiClient.post<ImportJobResult>("/imports/spatial-assignments", request);
    return response.data;
};

/**
 * Triggers an Address Grid Aggregations job (pre-computes geohash grid aggregations for map performance)
 */
export const triggerAddressGridAggregations = async (
    request: AddressGridAggregationsRequest
): Promise<ImportJobResult> => {
    const response = await apiClient.post<ImportJobResult>("/imports/address-grid-aggregations", request);
    return response.data;
};

/**
 * Triggers job(s) to fill electoral metadata (EU24, KF22, RD22, RF22, Kryss22) from val.se.
 * Requires Admin or above.
 * Pass either municipalityId (Guid för en kommun) eller lanId (2 siffror, t.ex. "01" för Stockholm) för hela länet.
 */
export const triggerElectoralMetadataFill = async (params: {
    municipalityId?: string;
    lanId?: string;
}): Promise<ImportJobResult | ElectoralMetadataFillForLanResult> => {
    const response = await apiClient.post<ImportJobResult | ElectoralMetadataFillForLanResult>(
        "/electoral-metadata/fill",
        undefined,
        { params: params.municipalityId ? { municipalityId: params.municipalityId } : { lanId: params.lanId } }
    );
    return response.data;
};

/**
 * Triggers job(s) to fill only KF18 (kommunval 2018) from historik.val.se.
 * Requires Admin or above. Same params as fill (municipalityId or lanId).
 */
export const triggerElectoralMetadataFill2018 = async (params: {
    municipalityId?: string;
    lanId?: string;
}): Promise<ImportJobResult | ElectoralMetadataFillForLanResult> => {
    const response = await apiClient.post<ImportJobResult | ElectoralMetadataFillForLanResult>(
        "/electoral-metadata/fill-2018",
        undefined,
        { params: params.municipalityId ? { municipalityId: params.municipalityId } : { lanId: params.lanId } }
    );
    return response.data;
};

/**
 * React Query hook for triggering OSM import
 */
export const useTriggerOsmImport = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: triggerOsmImport,
        onSuccess: () => {
            // Invalidate any relevant queries
            queryClient.invalidateQueries({ queryKey: ["imports"] });
        },
    });
};

/**
 * React Query hook for triggering Lantmäteriet import
 */
export const useTriggerLantmaterietImport = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: triggerLantmaterietImport,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["imports"] });
        },
    });
};

/**
 * React Query hook for triggering Electoral District import
 */
export const useTriggerElectoralDistrictImport = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: triggerElectoralDistrictImport,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["imports"] });
        },
    });
};

/**
 * React Query hook for triggering Postal Code import
 */
export const useTriggerPostalCodeImport = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: triggerPostalCodeImport,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["imports"] });
        },
    });
};

/**
 * React Query hook for triggering Municipality import
 */
export const useTriggerMunicipalityImport = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: triggerMunicipalityImport,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["imports"] });
        },
    });
};

/**
 * React Query hook for triggering Municipality from Electoral Districts (rebuild kommunpolygoner från valdistrikt)
 */
export const useTriggerMunicipalityFromElectoralDistricts = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: triggerMunicipalityFromElectoralDistricts,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["imports"] });
            queryClient.invalidateQueries({ queryKey: ["map", "municipalities"] });
        },
    });
};

/**
 * React Query hook for triggering Spatial Assignment
 */
export const useTriggerSpatialAssignment = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: triggerSpatialAssignment,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["imports"] });
            queryClient.invalidateQueries({ queryKey: ["assignments"] });
        },
    });
};

/**
 * React Query hook for triggering Address Grid Aggregations
 */
export const useTriggerAddressGridAggregations = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: triggerAddressGridAggregations,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["imports"] });
            queryClient.invalidateQueries({ queryKey: ["map", "addresses", "aggregations"] });
        },
    });
};

/**
 * React Query hook for triggering Electoral Metadata Fill (EU24, KF22, RD22, RF22, Kryss22 from val.se)
 */
export const useTriggerElectoralMetadataFill = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: triggerElectoralMetadataFill,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["imports"] });
            queryClient.invalidateQueries({ queryKey: ["electoral-metadata"] });
        },
    });
};

/**
 * React Query hook for triggering Electoral Metadata Fill 2018 (KF18 from historik.val.se)
 */
export const useTriggerElectoralMetadataFill2018 = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: triggerElectoralMetadataFill2018,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["imports"] });
            queryClient.invalidateQueries({ queryKey: ["electoral-metadata"] });
        },
    });
};